import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { NodeValues, NodeSpecItem, OpenModal } from "../../../ThermalDesignTypes";
import { useThermalDesign, DEFAULT_COATING_CONFIG, DEFAULT_NODE_VALUES } from "../../../ThermalDesignContext";
import MissionService from "../../../../../../../service/MissionService";
import { showToastMessage } from "../../../../../../../utils/commonFunctions";
import { DEFAULT_NODE_COUNT, MAX_NODE_COUNT, DEFAULT_ENVIRONMENTAL_PARAMETERS } from "../../../ThermalDesignContext";
import isEmpty from 'lodash/isEmpty';
import thermalConfigAuto from "../constants/thermalConfigAuto.json";

import {
  convertBackendToFrontend,
  convertFrontendToBackend,
  validateThermalConfig,
  extractEnvironmentalParameters,
  readFileAsText,
  exportSurfaceThresholdsAsJson,
  exportSurfaceThresholdsAsCsv,
  parseSurfaceThresholdsCsv,
  applySurfaceThresholdsToThermalModel,
} from "../utils/thermalConfigUtils";
import type { ThermalModelConfig } from "../types/ThermalConfig";
import type { SubsystemThresholdsMap, Thresholds } from "../types/SurfaceThresholdsTypes";
import { createEmptyThresholds } from "../types/SurfaceThresholdsTypes";
import {
  PANEL_THRESHOLDS_DATA,
  SUBSYSTEM_THRESHOLDS_DATA,
} from "../constants/surfaceThresholdsData";

export const getDefaultNodeValues = (length: number): NodeValues => ({
  ...DEFAULT_NODE_VALUES,
  node_conductance: Array(length).fill(0),
});

export const resizeConductance = (conductance: number[] | undefined | null, targetLength: number): number[] => {
  const base = Array.isArray(conductance) ? conductance : [];
  const resized = base.slice(0, targetLength);
  while (resized.length < targetLength) resized.push(0);
  return resized;
};

export const buildNodesWithNewNode = (
  prev: NodeSpecItem[],
  newNode: NodeSpecItem,
  nextCount: number
): NodeSpecItem[] => {
  const normalizedPrev = prev.map((node) => {
    const values = node.values ?? getDefaultNodeValues(nextCount);
    return {
      ...node,
      isSelected: false,
      values: {
        ...values,
        node_conductance: resizeConductance(values.node_conductance, nextCount),
      },
    };
  });
  return [...normalizedPrev, newNode];
};

const THERMAL_TOAST_ID = 'thermal-validation-toast';
const MAX_CONDUCTANCE = 1000;
const showThermalToast = (message: string) => {
  toast.dismiss(THERMAL_TOAST_ID);
  toast.info(message, {
    toastId: THERMAL_TOAST_ID,
    className: 'custom-toast-info',
    icon: true,
    autoClose: 5000,
  });
};

export const validateConductanceMatrix = (nodeLabels: string[], matrix: number[][]): string[] => {
  const n = nodeLabels.length;
  const errors: string[] = [];

  for (let i = 0; i < n; i++) {
    const row = matrix[i];
    if (!Array.isArray(row) || row.length !== n) {
      errors.push(`"${nodeLabels[i]}": conductance array must have ${n} elements (got ${row?.length ?? 0}).`);
      continue;
    }
    for (let j = 0; j < n; j++) {
      const v = row[j];
      if (typeof v !== 'number' || !isFinite(v)) {
        errors.push(`"${nodeLabels[i]}" at position ${j}: must be a finite number.`);
        continue;
      }
      if (v < 0) {
        errors.push(`"${nodeLabels[i]}" → "${nodeLabels[j]}": conductance cannot be negative (got ${v}).`);
      } else if (v > MAX_CONDUCTANCE) {
        errors.push(`"${nodeLabels[i]}" → "${nodeLabels[j]}": conductance exceeds maximum of ${MAX_CONDUCTANCE} W/K (got ${v}).`);
      }
      if (i === j && v !== 0) {
        errors.push(`"${nodeLabels[i]}": self-conductance (diagonal) must be 0 (got ${v}).`);
      }
    }
  }

  // Only check symmetry when no length errors exist (avoids false positives from incomplete rows)
  if (!errors.some(e => e.includes('must have'))) {
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const gij = matrix[i][j];
        const gji = matrix[j][i];
        if (Math.abs(gij - gji) > 1e-9) {
          errors.push(`Matrix not symmetric: "${nodeLabels[i]}" → "${nodeLabels[j]}" = ${gij} but "${nodeLabels[j]}" → "${nodeLabels[i]}" = ${gji}.`);
        }
      }
    }
  }

  return errors;
};

export const showConductanceErrors = (errors: string[]): void => {
  if (errors.length === 0) return;
  const suffix = errors.length > 1 ? ` (+${errors.length - 1} more)` : "";
  showThermalToast(errors[0] + suffix);
};

export const useConductanceCellInput = (
  initialValue: number,
  onCommit?: (value: number) => void
) => {
  const [localValue, setLocalValue] = useState(initialValue.toString());

  useEffect(() => {
    setLocalValue(initialValue.toString());
  }, [initialValue]);

  const handleChange = useCallback((rawValue: string) => {
    let raw = rawValue.replace(/[^0-9.]/g, "");
    const dotIdx = raw.indexOf(".");
    if (dotIdx !== -1) {
      raw = raw.slice(0, dotIdx + 1) + raw.slice(dotIdx + 1).replace(/\./g, "");
      raw = raw.slice(0, dotIdx + 6);
    }
    setLocalValue(raw);
  }, []);

  const handleBlur = useCallback(() => {
    const num = parseFloat(localValue);
    if (localValue === "" || isNaN(num) || num < 0) {
      if (!isNaN(num) && num < 0) {
        showThermalToast("Conductance cannot be negative. Value reset to 0.");
      } else if (localValue !== "" && isNaN(num)) {
        showThermalToast("Conductance must be a valid non-negative number. Value reset to 0.");
      }
      setLocalValue("0");
      onCommit?.(0);
      return;
    }
    if (num > MAX_CONDUCTANCE) {
      showThermalToast(`Conductance cannot exceed ${MAX_CONDUCTANCE} W/K. Value clamped to ${MAX_CONDUCTANCE}.`);
      setLocalValue(MAX_CONDUCTANCE.toString());
      onCommit?.(MAX_CONDUCTANCE);
      return;
    }
    setLocalValue(num.toString());
    onCommit?.(num);
  }, [localValue, onCommit]);

  return { localValue, handleChange, handleBlur };
};

type ModalKey = "isNodeSpecsModalOpen" | "isNodeSpecsJsonModalOpen" | "isThermalConductivityModalOpen" | "isThermalConductivityJsonModalOpen" | "isCustomMaterialModalOpen" | "isCoatingSettingsModalOpen" | "isConfigureCoatingModalOpen" | "isSubSystemSettingsModalOpen" | "isSurfaceThresholdsModalOpen";

let _previousSidebarState = true;

export const useHandleModalOpen = () => {
  const { setOpenModal, setIsSatDesignerSidebarOpen, isSatDesignerSidebarOpen } = useThermalDesign();

  const handleModalOpen = (modal: ModalKey) => {
    _previousSidebarState = isSatDesignerSidebarOpen;
    setIsSatDesignerSidebarOpen(false);
    const baseModalState = {
      isNodeSpecsModalOpen: false,
      isNodeSpecsJsonModalOpen: false,
      isThermalConductivityModalOpen: false,
      isThermalConductivityJsonModalOpen: false,
      isCustomMaterialModalOpen: false,
      isCoatingSettingsModalOpen: false,
      isConfigureCoatingModalOpen: false,
      isSubSystemSettingsModalOpen: false,
      isSurfaceThresholdsModalOpen: false,
      activeCoatingType: null,
      activeBrush: null,
      activeSubsystemId: null,
    };
    const childModalKeys = ["isCustomMaterialModalOpen", "isCoatingSettingsModalOpen", "isConfigureCoatingModalOpen"];
    const newState =
      childModalKeys.includes(modal)
        ? { ...baseModalState, isNodeSpecsModalOpen: true, [modal]: true }
        : { ...baseModalState, [modal]: true };
    setOpenModal(newState);
  };

  const restoreSidebar = () => {
    setIsSatDesignerSidebarOpen(_previousSidebarState);
  };

  return { handleModalOpen, restoreSidebar };
};

export const useModalAutoClose = (
  currentModalKey: keyof OpenModal,
  excludeModalKeys: string[] = []
) => {
  const { openModal, setOpenModal } = useThermalDesign();

  useEffect(() => {
    const allModalKeys = [
      "isNodeSpecsJsonModalOpen",
      "isThermalConductivityModalOpen",
      "isThermalConductivityJsonModalOpen",
      "isCustomMaterialModalOpen",
      "isCoatingSettingsModalOpen",
      "isConfigureCoatingModalOpen",
      "isSubSystemSettingsModalOpen",
    ] as const;

    const shouldClose = allModalKeys.some(
      (key) =>
        key !== currentModalKey &&
        !excludeModalKeys.includes(key) &&
        openModal[key]
    );

    if (shouldClose && openModal[currentModalKey]) {
      setOpenModal((prev) => ({
        ...prev,
        [currentModalKey]: false,
      }));
    }
  }, [openModal, setOpenModal, currentModalKey, excludeModalKeys]);
};

export const useUpdateSelectedNodes = () => {
  const { setNodeSpecsOptions } = useThermalDesign();

  const updateSelectedNodes = <K extends keyof NodeValues>(field: K, value: NodeValues[K]) => {
    setNodeSpecsOptions((prev) =>
      prev.map((node) => {
        if (!node.isSelected) return node;
        const existingValues = node.values ?? getDefaultNodeValues(prev.length);
        return {
          ...node,
          values: {
            ...existingValues,
            [field]: value,
          },
        };
      })
    );
  };


  return { updateSelectedNodes };
};

export const useThermalConfigLoader = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setEnvironmentalParametersOptions, setCount, setNodeSpecsOptions, nodeSpecsOptions, materialsData, setThermalModel, setConfigComplete } = useThermalDesign();

  const loadThermalConfig = useCallback(
    async (missionId: string, satelliteGroupId: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await MissionService.getThermalConfig(
          missionId,
          satelliteGroupId
        );
        const backendConfig = response.data;

        // Store the loaded thermal model to preserve subsystems during save
        setThermalModel(backendConfig.thermalModel);

        // Track whether the backend's config is marked as complete
        setConfigComplete(backendConfig.configComplete === true);

        // Face-based model: always keep 6 external + 2 internal deck items
        setCount(DEFAULT_NODE_COUNT);

        // If thermal config is not yet configured (configComplete === false),
        // use the auto-populated defaults from thermalConfigAuto.json.
        // Otherwise, convert the backend config to frontend shape normally.
        if (backendConfig.configComplete === false) {
          setNodeSpecsOptions(thermalConfigAuto as NodeSpecItem[]);
        } else {
          const normalizedBackendConfig = {
            ...backendConfig,
            count: Math.min(backendConfig.count ?? DEFAULT_NODE_COUNT, MAX_NODE_COUNT),
          };
          const frontendNodes = convertBackendToFrontend(normalizedBackendConfig, nodeSpecsOptions, materialsData);
          setNodeSpecsOptions(frontendNodes);
        }

        const extractedParams = extractEnvironmentalParameters(backendConfig, DEFAULT_ENVIRONMENTAL_PARAMETERS);
        setEnvironmentalParametersOptions(extractedParams);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to load thermal config";
        setError(message);
        setCount(DEFAULT_NODE_COUNT);
        setConfigComplete(false);
      } finally {
        setIsLoading(false);
      }
    },
    [setEnvironmentalParametersOptions, setCount, setNodeSpecsOptions, materialsData, setThermalModel, setConfigComplete]
  );

  return { loadThermalConfig, isLoading, error };
};

export const useThermalConfigSaver = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { nodeSpecsOptions, environmentalParametersOptions, count, thermalModel, setConfigComplete } =
    useThermalDesign();

  const saveThermalConfig = useCallback(
    async (missionId: string, satelliteGroupId: string, nodeOverride?: NodeSpecItem[]) => {
      setIsSaving(true);
      setError(null);

      try {
        const nodesToSave = nodeOverride ?? nodeSpecsOptions;
        if (nodesToSave.length < 4) {
          throw new Error("At least 4 nodes are required to save the thermal configuration");
        }

        // All non-unavailable nodes must be fully configured before saving,
        // regardless of nodes_per_face (1 or 9 — expansion handled by backend).
        const unconfiguredNodes = nodesToSave.filter(
          (n) => n.status !== "complete" && n.status !== "unavailable"
        );
        if (unconfiguredNodes.length > 0) {
          const names = unconfiguredNodes.map((n) => n.name).join(", ");
          throw new Error(`All nodes must be fully configured before saving. Unconfigured: ${names}`);
        }

        // Always send 7-node payload; nodes_per_face tells the backend how to expand.
        // Preserve existing subsystem placements
        const backendConfig = convertFrontendToBackend(
          nodesToSave,
          environmentalParametersOptions,
          thermalModel  // Pass existing thermal model to preserve subsystems
        );

        const validation = validateThermalConfig(backendConfig);
        if (!validation.isValid) {
          throw new Error(
            `Validation failed: ${validation.errors.join(", ")}`
          );
        }

        await MissionService.putThermalConfig(
          missionId,
          satelliteGroupId,
          backendConfig
        );
        setConfigComplete(true);
        showToastMessage('success', 'Thermal configuration saved successfully');
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to save thermal config";
        setError(message);
        console.error("Error saving thermal config:", err);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [nodeSpecsOptions, environmentalParametersOptions, count, thermalModel, setConfigComplete]
  );

  return { saveThermalConfig, isSaving, error };
};

export const FIELD_VALIDATION: Record<string, { min: number; max: number; default: number; mustBeGreaterThanZero?: boolean; isArray?: boolean }> = {
  node_mass: { min: 0, max: 5000, default: 1, mustBeGreaterThanZero: true },
  node_surface_area: { min: 0, max: 1000, default: 1, mustBeGreaterThanZero: true },
  node_specific_heat: { min: 100, max: 14000, default: 0, mustBeGreaterThanZero: true },
  node_emissivity: { min: 0, max: 1, default: 0, mustBeGreaterThanZero: true },
  node_absorptivity: { min: 0, max: 1, default: 0 },
  node_initial_temperature: { min: -200, max: 200, default: 0 },
  node_body_vec: { min: -1, max: 1, default: 0, isArray: true },
  node_internal_power: { min: 0, max: 20000, default: 0 },
};

export const isFieldValid = (fieldName: string, value: any): boolean => {
  if (!(fieldName in FIELD_VALIDATION)) {
    return true;
  }

  const config = FIELD_VALIDATION[fieldName];

  if (config.isArray && Array.isArray(value)) {
    return value.every((item) => typeof item === "number" && item >= config.min && item <= config.max);
  }

  if (typeof value === "number" && isFinite(value)) {
    const isInRange = value >= config.min && value <= config.max;
    if (config.mustBeGreaterThanZero) {
      return isInRange && value > 0;
    }
    return isInRange;
  }

  return false;
};

export const validateSelectedNode = (node: any): Set<string> => {
  const invalid = new Set<string>();

  if (!node || !node.values) return invalid;

  const isInternal = node.values.node_isInternal ?? false;
  const hasMaterial = !!(node.values.node_material && node.values.node_material !== "");

  const materialDependentFields = new Set(["node_specific_heat", "node_absorptivity", "node_emissivity"]);
  const internalZeroFields = new Set(["node_emissivity", "node_absorptivity"]);

  Object.keys(FIELD_VALIDATION).forEach((key) => {
    if (key in node.values) {
      if (!hasMaterial && materialDependentFields.has(key)) return;
      if (isInternal && internalZeroFields.has(key)) {
        const config = FIELD_VALIDATION[key];
        const val = node.values[key];
        if (typeof val !== "number" || val < config.min || val > config.max) {
          invalid.add(key);
        }
      } else if (!isFieldValid(key, node.values[key])) {
        invalid.add(key);
      }
    }
  });

  const material = node.values.node_material;
  if (!material || material === "") {
    invalid.add("node_material");
  }

  return invalid;
};

export const showInvalidFieldToasts = (invalidFields: Set<string>, nodeName: string = "") => {
  const nodePrefix = nodeName ? `[${nodeName}] ` : "";

  const labels: Record<string, string> = {
    node_mass: "Mass",
    node_surface_area: "Surface Area",
    node_specific_heat: "Specific Heat",
    node_absorptivity: "Absorptivity",
    node_emissivity: "Emissivity",
    node_initial_temperature: "Initial Temperature",
    node_internal_power: "Internal Power",
  };
  const units: Record<string, string> = {
    node_mass: "kg",
    node_surface_area: "m²",
    node_specific_heat: "J/(g·K)",
    node_absorptivity: "",
    node_emissivity: "",
    node_initial_temperature: "°C",
    node_internal_power: "W",
  };

  const getFieldMessage = (fieldName: string): string | null => {
    if (fieldName === "node_material") return `${nodePrefix}Please select a material`;
    const config = FIELD_VALIDATION[fieldName];
    if (!config) return null;
    if (fieldName === "node_body_vec") return `${nodePrefix}Direction (Body Vec) must have values between ${config.min} and ${config.max}`;
    const label = labels[fieldName] || fieldName;
    const unit = units[fieldName];
    const unitSuffix = unit ? ` ${unit}` : "";
    return `${nodePrefix}${label} must be between ${config.min}${unitSuffix} and ${config.max}${unitSuffix}`;
  };

  const fieldArray = Array.from(invalidFields);
  const firstMessage = getFieldMessage(fieldArray[0]);
  if (!firstMessage) return;

  const remaining = fieldArray.length - 1;
  const suffix = remaining > 0 ? ` (+${remaining} more invalid field${remaining > 1 ? "s" : ""})` : "";
  showThermalToast(`${firstMessage}${suffix}`);
};

export const useNodeSpecsModal = () => {
  const { nodeSpecsOptions, setNodeSpecsOptions, materialsData, setOpenModal } = useThermalDesign();
  const { updateSelectedNodes } = useUpdateSelectedNodes();
  const { saveThermalConfig } = useThermalConfigSaver();
  const { restoreSidebar } = useHandleModalOpen();

  // Snapshot of nodeSpecsOptions at the time the modal mounts — used to restore on cancel
  const initialSnapshotRef = useRef<typeof nodeSpecsOptions>(nodeSpecsOptions);

  const [isModalMinimized, setIsModalMinimized] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState<string | null>(null);
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

  const convertToNumber = (value: string | undefined | null): number => {
    if (isEmpty(value)) return 0;
    return Number(value);
  };

  const getNextAvailableNode = (currentNodeId: string): string | null => {
    const currentIndex = nodeSpecsOptions.findIndex((node) => node.id === currentNodeId);
    for (let i = currentIndex + 1; i < nodeSpecsOptions.length; i++) {
      if (nodeSpecsOptions[i].status !== "unavailable") {
        return nodeSpecsOptions[i].id;
      }
    }
    return null;
  };

  const selectedNode = useMemo(
    () => nodeSpecsOptions.find((node) => node.isSelected),
    [nodeSpecsOptions]
  );

  const isLastAvailableNode = useCallback((): boolean => {
    if (!selectedNode) return false;
    return getNextAvailableNode(selectedNode.id) === null;
  }, [selectedNode]);

  const markNodeAsComplete = useCallback(() => {
    if (!selectedNode) return;
    setNodeSpecsOptions((prev) =>
      prev.map((node) =>
        node.id === selectedNode.id
          ? { ...node, status: "complete" }
          : node
      )
    );
  }, [selectedNode, setNodeSpecsOptions]);

  const handleSaveAndExit = useCallback(
    async (mission_id: string, sat_id: string) => {
      if (!selectedNode) return;

      // Validate EVERY non-unavailable node, not just the selected one.
      // This prevents a node with invalid values that was skipped via navigation
      // from being silently submitted when Save is clicked on a different node.
      let hasAnyInvalid = false;
      nodeSpecsOptions.forEach((node) => {
        if (node.status === "unavailable") return;
        const invalid = validateSelectedNode(node);
        if (invalid.size > 0) {
          showInvalidFieldToasts(invalid, node.name);
          hasAnyInvalid = true;
          // Highlight the invalid fields if this happens to be the currently selected node
          if (node.id === selectedNode.id) {
            setInvalidFields(invalid);
          }
        }
      });

      if (hasAnyInvalid) return;

      // All nodes are valid — clear any stale field highlights
      setInvalidFields(new Set());

      try {
        if (mission_id && sat_id) {
          // Mark the currently-selected node as complete synchronously so the
          // save call sees the updated status (React state updates are async).
          const updatedNodes = nodeSpecsOptions.map((node) =>
            node.id === selectedNode.id ? { ...node, status: "complete" as const } : node
          );
          setNodeSpecsOptions(updatedNodes);

          // Execute the PUT call, passing the updated nodes directly to avoid
          // reading stale context state inside saveThermalConfig.
          await saveThermalConfig(mission_id, sat_id, updatedNodes);
          restoreSidebar();
          setOpenModal((prev) => ({
            ...prev,
            isNodeSpecsModalOpen: false,
          }));
        }
      } catch (err) {
        console.error("Failed to save thermal config:", err);
        showToastMessage('error', 'Failed to save thermal configuration');
      }
    },
    [selectedNode, nodeSpecsOptions, markNodeAsComplete, saveThermalConfig, setOpenModal]
  );

  const handleSaveAndNext = useCallback(() => {
    if (!selectedNode) return;

    const invalid = validateSelectedNode(selectedNode);
    setInvalidFields(invalid);

    if (invalid.size > 0) {
      showInvalidFieldToasts(invalid, selectedNode.name);
      return;
    }

    markNodeAsComplete();

    const nextNodeId = getNextAvailableNode(selectedNode.id);
    if (nextNodeId) {
      handleSelectNode(nextNodeId);
    }
  }, [selectedNode, markNodeAsComplete]);

  const handleSelectNode = useCallback((id: string) => {
    // Reset invalid fields when switching nodes so previous errors don't bleed into the newly selected node
    setInvalidFields(new Set());
    setNodeSpecsOptions((prev) =>
      prev.map((node) => ({
        ...node,
        isSelected: node.id === id,
      }))
    );
  }, [setNodeSpecsOptions, setInvalidFields]);

  const handleDeleteNode = useCallback((nodeId: string) => {
    // Check if deletion is allowed (must have more than 4 nodes remaining)
    if (nodeSpecsOptions.length <= 4) {
      showToastMessage('info', 'Cannot delete node: minimum of 4 nodes required');
      return;
    }

    setNodeToDelete(nodeId);
    setIsDeleteConfirmationOpen(true);
  }, [nodeSpecsOptions.length]);

  const handleConfirmDelete = useCallback(() => {
    if (!nodeToDelete) return;

    const nodeToDeleteIndex = nodeSpecsOptions.findIndex((node) => node.id === nodeToDelete);

    let nodeToSelectIndex = nodeToDeleteIndex - 1;
    if (nodeToSelectIndex < 0) {
      nodeToSelectIndex = 0;
    }

    // Remove the node and renumber remaining nodes for consistent naming
    const updatedNodes = nodeSpecsOptions
      .filter((_, index) => index !== nodeToDeleteIndex)
      .map((node, index) => ({
        ...node,
        id: String(index + 1),
        name: `Node ${String(index + 1).padStart(2, "0")}`,
        isSelected: index === nodeToSelectIndex,
      }));

    setNodeSpecsOptions(updatedNodes);
    setIsDeleteConfirmationOpen(false);
    setNodeToDelete(null);
    showToastMessage('success', 'Node deleted successfully');
  }, [nodeToDelete, nodeSpecsOptions, setNodeSpecsOptions]);

  const handleCancelDelete = useCallback(() => {
    setIsDeleteConfirmationOpen(false);
    setNodeToDelete(null);
  }, []);

  const toggleIsInternal = useCallback(() => {
    if (selectedNode) {
      updateSelectedNodes("node_isInternal", !selectedNode.values?.node_isInternal);
    }
  }, [selectedNode, updateSelectedNodes]);

  const handleAddNode = useCallback(() => {
    if (nodeSpecsOptions.length >= MAX_NODE_COUNT) return;

    const nextCount = nodeSpecsOptions.length + 1;
    const newNode: typeof nodeSpecsOptions[0] = {
      name: `Node ${String(nextCount).padStart(2, "0")}`,
      id: String(nextCount),
      status: "unconfigured",
      isSelected: true,
      values: { ...getDefaultNodeValues(nextCount) },
    };

    // Deselect all other nodes, resize every conductance array to nextCount, then append the new node
    setNodeSpecsOptions((prev) => buildNodesWithNewNode(prev, newNode, nextCount));
    showToastMessage('success', 'Node added successfully');
  }, [nodeSpecsOptions.length, setNodeSpecsOptions]);

  const handleMaterialSelection = useCallback(    (materialName: string) => {
      // Clear the material invalid flag when user selects a material
      setInvalidFields((prev) => {
        if (!prev.has("node_material")) return prev;
        const next = new Set(prev);
        next.delete("node_material");
        return next;
      });

      const selectedMaterialData = materialsData.find((m) => m.material_name === materialName);

      // Apply all material fields in a single atomic setNodeSpecsOptions call to
      // avoid multiple chained updaters and ensure every field — including zero
      // values — is always written (the old guard `if (value !== 0)` caused stale
      // values to persist when switching to a material with a zero property).
      setNodeSpecsOptions((prev) =>
        prev.map((node) => {
          if (!node.isSelected) return node;
          const existingValues = node.values ?? getDefaultNodeValues(prev.length);
          const materialProps = selectedMaterialData
            ? {
              node_material: materialName,
              node_specific_heat: selectedMaterialData.specific_heat ?? 0,
              node_absorptivity: selectedMaterialData.absorptivity ?? 0,
              node_emissivity: selectedMaterialData.emissivity ?? 0,
              node_material_properties: {
                material_name: selectedMaterialData.material_name,
                specific_heat: selectedMaterialData.specific_heat ?? 0,
                thermal_conductivity: selectedMaterialData.thermal_conductivity ?? 0,
                density: selectedMaterialData.density ?? 0,
                absorptivity: selectedMaterialData.absorptivity ?? 0,
                emissivity: selectedMaterialData.emissivity ?? 0,
                youngs_modulus: selectedMaterialData.youngs_modulus ?? 0,
                yield_strength: selectedMaterialData.yield_strength ?? 0,
                cte: selectedMaterialData.cte ?? 0,
              },
            }
            : { node_material: materialName };
          return { ...node, values: { ...existingValues, ...materialProps } };
        })
      );
    },
    [materialsData, setNodeSpecsOptions, setInvalidFields]
  );

  const handleCancelConfirm = useCallback(() => {
    setNodeSpecsOptions(initialSnapshotRef.current);
    setOpenModal((prev) => ({ ...prev, isNodeSpecsModalOpen: false }));
  }, [setNodeSpecsOptions, setOpenModal]);

  return {
    isModalMinimized,
    setIsModalMinimized,
    isDeleteConfirmationOpen,
    setIsDeleteConfirmationOpen,
    nodeToDelete,
    setNodeToDelete,
    invalidFields,
    setInvalidFields,
    selectedNode,
    convertToNumber,
    getNextAvailableNode,
    isLastAvailableNode,
    markNodeAsComplete,
    handleSaveAndExit,
    handleSaveAndNext,
    handleSelectNode,
    handleDeleteNode,
    handleConfirmDelete,
    handleCancelDelete,
    toggleIsInternal,
    handleAddNode,
    handleMaterialSelection,
    restoreSidebar,
    handleCancelConfirm,
  };
};

export const useSurfaceThresholdsModal = () => {
  const { thermalModel, setThermalModel, setOpenModal } = useThermalDesign();
  const { restoreSidebar } = useHandleModalOpen();
  const { saveThermalConfig } = useThermalConfigSaver();

  const [isModalMinimized, setIsModalMinimized] = useState(false);

  // Snapshot of thermalModel at the time the modal mounts — used to restore on cancel
  const initialSnapshotRef = useRef<ThermalModelConfig | undefined>(thermalModel);

  // Guard to seed defaults only once per mount
  const hasSeededRef = useRef(false);

  // Seed defaults on mount: fill any missing panel/subsystem thresholds with constants
  useEffect(() => {
    if (!thermalModel || hasSeededRef.current) return;
    hasSeededRef.current = true;

    setThermalModel((prev) => {
      if (!prev) return prev;

      const panels = prev.panels ?? [];
      const updatedPanels = panels.map((panel) =>
        panel.thresholds === undefined
          ? { ...panel, thresholds: PANEL_THRESHOLDS_DATA[panel.panelLabel] ?? createEmptyThresholds() }
          : panel
      );

      const updatedSubsystemThresholds = { ...prev.subsystemThresholds };
      const seenSubsystemNames = new Set<string>();
      panels.forEach((panel) =>
        (panel.cells ?? []).forEach((cell) =>
          (cell.subsystems ?? []).forEach((sub) => {
            seenSubsystemNames.add(sub.displayName);
            if (updatedSubsystemThresholds[sub.displayName] === undefined) {
              updatedSubsystemThresholds[sub.displayName] =
                SUBSYSTEM_THRESHOLDS_DATA[sub.displayName] ?? createEmptyThresholds();
            }
          })
        )
      );

      return {
        ...prev,
        panels: updatedPanels,
        subsystemThresholds: updatedSubsystemThresholds,
      };
    });
  }, [thermalModel, setThermalModel]);

  // Memoized so repeated renders (e.g. selecting a different face) reuse the same
  // reference when thermalModel hasn't actually changed — otherwise `?? {}` would
  // create a new object every render and defeat memoization all the way down into
  // the modal's commitThreshold/columns/rows useMemo/useCallback chains.
  const subsystemThresholds: SubsystemThresholdsMap = useMemo(
    () => thermalModel?.subsystemThresholds ?? {},
    [thermalModel]
  );

  const setSubsystemThreshold = useCallback((displayName: string, thresholds: Thresholds) => {
    setThermalModel((prev) =>
      prev ? { ...prev, subsystemThresholds: { ...prev.subsystemThresholds, [displayName]: thresholds } } : prev
    );
  }, [setThermalModel]);

  // Sets a panel's own thresholds AND cascades the same values to every subsystem placed on that panel
  const applyPanelThreshold = useCallback((panelLabel: string, thresholds: Thresholds) => {
    setThermalModel((prev) => {
      if (!prev) return prev;
      const panels = prev.panels ?? [];
      const panel = panels.find((p) => p.panelLabel === panelLabel);
      if (!panel) return prev;

      const namesOnPanel = new Set<string>();
      (panel.cells ?? []).forEach((cell) => (cell.subsystems ?? []).forEach((sub) => namesOnPanel.add(sub.displayName)));

      const updatedSubsystemThresholds = { ...prev.subsystemThresholds };
      namesOnPanel.forEach((name) => { updatedSubsystemThresholds[name] = thresholds; });

      return {
        ...prev,
        panels: panels.map((p) => (p.panelLabel === panelLabel ? { ...p, thresholds } : p)),
        subsystemThresholds: updatedSubsystemThresholds,
      };
    });
  }, [setThermalModel]);

  const handleSave = useCallback(async (mission_id: string, sat_id: string) => {
    try {
      await saveThermalConfig(mission_id, sat_id);
      showToastMessage('success', 'Surface thresholds saved successfully');
      restoreSidebar();
      setOpenModal((prev) => ({ ...prev, isSurfaceThresholdsModalOpen: false }));
    } catch (err) {
      console.error('Failed to save surface thresholds:', err);
      showToastMessage('error', 'Failed to save surface thresholds');
    }
  }, [saveThermalConfig, restoreSidebar, setOpenModal]);

  const handleCancel = useCallback(() => {
    setThermalModel(initialSnapshotRef.current);
    restoreSidebar();
    setOpenModal((prev) => ({ ...prev, isSurfaceThresholdsModalOpen: false }));
  }, [setThermalModel, restoreSidebar, setOpenModal]);

  const handleExportJson = useCallback(() => {
    exportSurfaceThresholdsAsJson(thermalModel);
  }, [thermalModel]);

  const handleExportCsv = useCallback(() => {
    exportSurfaceThresholdsAsCsv(thermalModel);
  }, [thermalModel]);

  const handleImport = useCallback(async (file: File) => {
    if (!thermalModel) {
      showToastMessage('error', 'Import failed: thermal model is not loaded yet');
      return;
    }
    try {
      const text = await readFileAsText(file);
      const parsed: SubsystemThresholdsMap = file.name.toLowerCase().endsWith(".csv")
        ? parseSurfaceThresholdsCsv(text)
        : JSON.parse(text);
      setThermalModel(applySurfaceThresholdsToThermalModel(parsed, thermalModel));
      showToastMessage('success', 'Surface thresholds imported successfully');
    } catch {
      showToastMessage('error', 'Import failed: could not parse file');
    }
  }, [thermalModel, setThermalModel]);

  return {
    isModalMinimized,
    setIsModalMinimized,
    thermalModel,
    subsystemThresholds,
    setSubsystemThreshold,
    applyPanelThreshold,
    handleSave,
    handleCancel,
    handleExportJson,
    handleExportCsv,
    handleImport,
  };
};

// ============ THERMAL SETTINGS & VISIBILITY HOOKS ============

/**
 * Determines if the thermal coating toggle should be disabled
 * Disabled only when the selected node is not external
 */
export const useThermalCoatingToggleDisabled = (): boolean => {
  const { nodeSpecsOptions } = useThermalDesign();
  
  return useMemo(() => {
    const selectedNode = nodeSpecsOptions.find((n) => n.isSelected);
    const isExternal = selectedNode && selectedNode.values && !selectedNode.values.node_isInternal;
    return !isExternal;
  }, [nodeSpecsOptions]);
};

/**
 * Determines if the subsystems toggle should be disabled
 * Accepts computed data from component to avoid circular dependencies
 */
export const useSubsystemsToggleDisabled = (visibleSubsystemsCount: number): boolean => {
  return useMemo(() => visibleSubsystemsCount === 0, [visibleSubsystemsCount]);
};

/**
 * Manages thermal coating section toggle state
 * Syncs internal state with context for bidirectional communication with ThermalSettingsBtn
 */
/**
 * Hook to get the currently selected node from the context (avoids duplicate .find() across ~9 files).
 */
export const useSelectedNode = () => {
  const { nodeSpecsOptions } = useThermalDesign();
  return useMemo(() => nodeSpecsOptions.find((n) => n.isSelected), [nodeSpecsOptions]);
};

export const useThermalCoatingToggle = (
  selectedNode: any,
  nodeSpecsOptions: NodeSpecItem[],
  setNodeSpecsOptions: (items: NodeSpecItem[]) => void
) => {
  const { setShowCoatingView } = useThermalDesign();
  const coatingBackupRef = useRef<Record<string, any>>({});
  
  const [enabled, setEnabled] = useState(false);

  const handleToggle = useCallback(() => {
    setEnabled((e) => {
      const next = !e;
      if (selectedNode) {
        setNodeSpecsOptions(
          nodeSpecsOptions.map((node) => {
            if (node.id !== selectedNode.id) return node;
            
            // When toggling ON, restore from backup if available, otherwise use existing or defaults
            if (next) {
              const backup = coatingBackupRef.current[node.id];
              return {
                ...node,
                values: node.values
                  ? {
                      ...node.values,
                      coating: backup || node.values.coating || { ...DEFAULT_COATING_CONFIG, cells: Array(9).fill("none") },
                    }
                  : undefined,
              };
            }
            
            // When toggling OFF, save current data to backup keyed by node ID
            coatingBackupRef.current[node.id] = node.values?.coating;
            return {
              ...node,
              values: node.values
                ? {
                    ...node.values,
                    coating: undefined,
                  }
                : undefined,
            };
          })
        );
      }
      // Sync with context
      setShowCoatingView(next);
      return next;
    });
  }, [selectedNode, nodeSpecsOptions, setNodeSpecsOptions, setShowCoatingView]);

  return { enabled, handleToggle };
};

/**
 * Manages subsystems section toggle state
 * Syncs both local state and context for bidirectional communication
 */
export const useSubsystemsToggleSync = (
  showSubsystemsView: boolean,
  setShowSubsystemsView: (show: boolean) => void
) => {
  const { showSubsystemView: contextShowSubsystemView, setShowSubsystemView: contextSetShowSubsystemView } = useThermalDesign();

  const handleToggle = useCallback(() => {
    setShowSubsystemsView(!showSubsystemsView);
    contextSetShowSubsystemView(!contextShowSubsystemView);
  }, [showSubsystemsView, contextShowSubsystemView, setShowSubsystemsView, contextSetShowSubsystemView]);

  return { handleToggle };
};

interface LayerItemConfig {
  id: string;
  checked: boolean;
  label?: string;
}

interface LayerDisabledStateResult {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
}

export const useLayerDisabledState = (
  layers: LayerItemConfig[],
  gizmoOn: boolean,
  subsystemsOn: boolean,
  isThermalCoatingDisabled: boolean,
  isSubsystemsDisabled: boolean
): LayerDisabledStateResult[] => {
  return useMemo(() => {
    return layers.map((l) => {
      let disabled = false;

      if (gizmoOn && (l.id === "nadir_axis" || l.id === "roll_pitch_yaw")) {
        disabled = true;
      }

      if (l.id === "thermal_coating_view") {
        disabled = isThermalCoatingDisabled;
      }

      if (l.id === "subsystems") {
        disabled = isSubsystemsDisabled;
      }

      if (l.id === "subsystem_labels") {
        disabled = !subsystemsOn;
      }

      return {
        id: l.id,
        label: l.label || l.id,
        checked: l.checked,
        disabled,
      };
    });
  }, [layers, gizmoOn, subsystemsOn, isThermalCoatingDisabled, isSubsystemsDisabled]);
};

export const useJsonModalState = (initialJson: string) => {
  const [json, setJson] = useState(initialJson);
  const [jsonError, setJsonError] = useState<string>("");
  const [confirmDialogState, setConfirmDialogState] = useState({
    isOpen: false,
    onConfirm: null as (() => void) | null,
  });

  const pendingJsonRef = useRef<string | null>(null);
  const hasEditedRef = useRef(false);

  const validateJsonStructure = useCallback(
    (parsedJson: any, originalJsonString: string): boolean => {
      try {
        const originalJson = JSON.parse(originalJsonString);

        // Handle arrays
        if (Array.isArray(parsedJson) && Array.isArray(originalJson)) {
          if (parsedJson.length !== originalJson.length) return false;
          for (let i = 0; i < parsedJson.length; i++) {
            const parsedKeys = Object.keys(parsedJson[i]).sort();
            const originalKeys = Object.keys(originalJson[i]).sort();
            if (JSON.stringify(parsedKeys) !== JSON.stringify(originalKeys)) {
              return false;
            }
          }
          return true;
        }

        // Handle objects
        if (typeof parsedJson === "object" && typeof originalJson === "object") {
          const parsedKeys = Object.keys(parsedJson).sort();
          const originalKeys = Object.keys(originalJson).sort();
          return JSON.stringify(parsedKeys) === JSON.stringify(originalKeys);
        }
        return false;
      } catch {
        return false;
      }
    },
    []
  );

  const reconstructJsonWithOriginalKeys = useCallback(
    (parsedJson: any, originalJsonString: string): any => {
      try {
        const originalJson = JSON.parse(originalJsonString);

        // Handle arrays
        if (Array.isArray(parsedJson) && Array.isArray(originalJson)) {
          return parsedJson.map((editedItem, index) => {
            const originalItem = originalJson[index] || {};
            const reconstructed: any = {};
            for (const key of Object.keys(originalItem)) {
              reconstructed[key] = editedItem[key] !== undefined ? editedItem[key] : originalItem[key];
            }
            return reconstructed;
          });
        }

        // Handle objects
        if (typeof parsedJson === "object" && typeof originalJson === "object") {
          const result: any = {};
          for (const key of Object.keys(originalJson)) {
            result[key] = parsedJson[key] !== undefined ? parsedJson[key] : originalJson[key];
          }
          return result;
        }

        return parsedJson;
      } catch {
        return parsedJson;
      }
    },
    []
  );

  const handleJsonBlur = useCallback(
    (currentJsonValue: string, onValidJson: (json: string) => void) => {
      const trimmed = currentJsonValue.trim();

      try {
        const parsedJson = JSON.parse(trimmed);

        if (!validateJsonStructure(parsedJson, initialJson)) {
          setJsonError("JSON structure has been modified. Only values can be changed.");
          return;
        }

        setJson(trimmed);
        setJsonError("");
        pendingJsonRef.current = null;
        onValidJson(trimmed);
        hasEditedRef.current = false;
      } catch (e: any) {
        setJsonError(`Invalid JSON: ${e.message}`);
      }
    },
    [initialJson, validateJsonStructure]
  );

  const handleJsonChange = useCallback((value: string | undefined) => {
    const newValue = value ?? "";
    setJson(newValue);
    hasEditedRef.current = true;
    pendingJsonRef.current = newValue;
    setJsonError("");
  }, []);

  return {
    json,
    setJson,
    jsonError,
    setJsonError,
    handleJsonBlur,
    handleJsonChange,
    confirmDialogState,
    setConfirmDialogState,
    pendingJsonRef,
    hasEditedRef,
    validateJsonStructure,
    reconstructJsonWithOriginalKeys,
  };
};
